import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, Lock, Mail, Eye, Info, Cookie, Settings, AlertCircle, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

const Privacy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
    
        <div className="prose max-w-none">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold m-0">Privacy Policy</h1>
          </div>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Info className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">1. Introduction</h2>
            </div>
            <p className="text-gray-700">
              Bright Stages, LLC ("we," "our," "us") is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
            </p>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">2. Information We Collect</h2>
            </div>
            <p className="text-gray-700 mb-4">We collect only minimal information necessary to provide our services effectively. We do not collect any personal identifying information about children.</p>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-2">A. Information You Provide</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Parent/Guardian Name (optional)</li>
                  <li>Email Address (required for account creation and communication)</li>
                  <li>Child's First Name or Nickname (optional for profile tracking)</li>
                  <li>Child's Age or Birth Month/Year (used for developmental tracking)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">B. Automatically Collected Information</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Device Information (e.g., browser type, operating system)</li>
                  <li>Usage Data (e.g., pages visited, time spent on the platform)</li>
                  <li>IP Address (for security and analytics)</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">3. How We Use Your Information</h2>
            </div>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Provide, maintain, and improve our services</li>
              <li>Track developmental progress based on research-backed assessments</li>
              <li>Offer personalized insights and recommendations</li>
              <li>Communicate with users regarding updates, services, and support</li>
              <li>Ensure security and prevent fraudulent activities</li>
            </ul>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">4. Data Sharing & Security</h2>
            </div>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>We do NOT sell, rent, or trade your data to third parties</li>
              <li>Your data is stored securely using encryption and industry-standard safeguards</li>
              <li>We may share limited data with trusted service providers (e.g., hosting services) to operate the platform effectively</li>
              <li>Data may be disclosed if required by law, in response to legal processes, or to protect the rights and safety of users</li>
            </ul>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Cookie className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">5. Cookies & Tracking Technologies</h2>
            </div>
            <p className="text-gray-700">
              We use cookies and similar tracking technologies to improve user experience and collect anonymous analytics data. You can manage cookie preferences through your browser settings.
            </p>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">6. Data Retention</h2>
            </div>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>We retain user data only as long as necessary to provide services and comply with legal obligations</li>
              <li>Users can request account deletion at any time by contacting us</li>
            </ul>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">7. Your Privacy Rights</h2>
            </div>
            <p className="text-gray-700 mb-4">Depending on your location, you may have rights to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Access, update, or delete your data</li>
              <li>Opt-out of certain data processing activities</li>
              <li>Request a copy of the information we store about you</li>
            </ul>
            <p className="mt-4 text-gray-700">To exercise these rights, email us at info@BrightStages.com.</p>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">8. Children's Privacy</h2>
            </div>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Bright Stages, LLC complies with COPPA (Children's Online Privacy Protection Act) and similar regulations</li>
              <li>We do not collect personal information from children under 13 without parental consent</li>
              <li>Parents can delete their child's information at any time from the Dashboard</li>
            </ul>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <ExternalLink className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">9. Third-Party Links</h2>
            </div>
            <p className="text-gray-700">
              Our platform may contain links to third-party websites. We are not responsible for their privacy practices, and we encourage users to review their privacy policies.
            </p>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Info className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">10. Changes to This Policy</h2>
            </div>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. Users will be notified of significant changes, and continued use of the platform constitutes acceptance of the updated terms.
            </p>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">11. Contact Us</h2>
            </div>
            <p className="text-gray-700">For any privacy-related inquiries, please contact us at:</p>
            <p className="text-gray-700">
              Email: info@BrightStages.com
            </p>
          </section>
        </div>

        {/* Footer Links */}
        <footer className="py-8 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0 text-sm md:text-base md:space-x-8">
              <div className="flex space-x-4 md:space-x-8">
                <Button 
                  variant="link" 
                  onClick={() => navigate("/privacy")} 
                  className="text-[#1A1F2C] font-semibold hover:text-[#005b96] transition-colors whitespace-nowrap"
                >
                  Privacy Policy
                </Button>
                <Button 
                  variant="link" 
                  onClick={() => navigate("/legal-information")} 
                  className="text-[#1A1F2C] font-semibold hover:text-[#005b96] transition-colors whitespace-nowrap"
                >
                  Legal Information
                </Button>
              </div>
              <Button 
                variant="link" 
                onClick={() => navigate("/terms")} 
                className="text-[#1A1F2C] font-semibold hover:text-[#005b96] transition-colors whitespace-nowrap"
              >
                Terms & Acknowledgments
              </Button>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Privacy;
